// File: packages/calculator/components/FeatureItem.tsx

import React from 'react';
import clsx from 'clsx';
import { Button, Tooltip } from '@dopomogai/ui';
import { AlertCircle, Clock, Info, Star } from 'lucide-react';
import type { Module } from '@dopomogai/types';
import { fmtMoney } from '@dopomogai/agent-kit';

type Props = {
  feature: Module;
  checked: boolean;
  disabledReason?: string;
  onToggle: (id: string) => void;
  onFix?: (id: string) => void;
};

/**
 * A single interactive row for a feature/module within a FeatureGroup.
 * Displays all details and handles user interaction.
 */
export function FeatureItem({ feature, checked, disabledReason, onToggle, onFix }: Props) {
  const isFixable = disabledReason?.toLowerCase().startsWith('requires:');
  const isDisabled = Boolean(disabledReason);

  return (
    <div
      id={`feature-wrapper-${feature.id}`}
      className={clsx(
        'flex items-start gap-4 p-3 border-2 border-black rounded-lg bg-white',
        isDisabled ? 'opacity-60' : 'hover:bg-slate-50/50'
      )}
      aria-disabled={isDisabled}
    >
      <input
        type="checkbox"
        id={`feature-checkbox-${feature.id}`}
        className="mt-1 w-5 h-5 accent-emerald-600 cursor-pointer disabled:cursor-not-allowed"
        checked={checked}
        onChange={() => onToggle(feature.id)}
        disabled={isDisabled}
      />
      <div className="flex-1">
        <label
          htmlFor={`feature-checkbox-${feature.id}`}
          className={clsx('font-semibold flex items-center gap-2 text-slate-900', !isDisabled && 'cursor-pointer')}
        >
          {feature.recommended && <Star className="w-4 h-4 text-amber-500" aria-label="Recommended" />}
          {feature.label}
          <Tooltip text={feature.desc}><Info size={16} className="text-slate-500" /></Tooltip>
        </label>

        <div className="flex items-center justify-between mt-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-700">
            <span className="font-mono font-semibold">{fmtMoney(feature.priceUSD, 'USD')}</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {feature.etaDays[0]}–{feature.etaDays[1]} days
            </span>
          </div>
        </div>

        {disabledReason && (
          <div className="mt-2 inline-flex items-center gap-2 text-amber-800 text-xs p-2 bg-amber-100/50 border border-amber-300 rounded-md">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{disabledReason}</span>
            {isFixable && (
              <Button variant="outline" size="sm" onClick={() => onFix?.(feature.id)}>
                Add all & select
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}